import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComp } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, User, MapPin } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { composeBusinessIso, formatBusinessDateTime } from "@/lib/businessTz";
import { NearbyDaysHint } from "@/components/NearbyDaysHint";

// Hour options in 12-hour format (12 AM = 0, 1 AM = 1, …, 11 PM = 23)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const h12 = i % 12 === 0 ? 12 : i % 12;
  const ampm = i < 12 ? "AM" : "PM";
  return { value: String(i), label: `${h12} ${ampm}` };
});
// Minute selects in 15-min increments
const MINUTE_OPTIONS = ["00", "15", "30", "45"].map((m) => ({ value: m, label: m }));

/**
 * ScheduleJobDialog
 * -----------------
 * Fast "Schedule Job" form triggered from an Estimate (detail page,
 * list row, or acceptance confirmation). Customer / service address /
 * services / totals / notes are inherited from the estimate on the
 * backend — this dialog only asks the scheduler questions:
 *   - Start date/time
 *   - Duration (minutes) — sets scheduled_end
 *   - Assigned technician/team (free text until a team module exists)
 *   - Service address override (optional)
 *   - Additional notes (appended to estimate notes)
 *
 * Idempotency: if the estimate already has scheduled_job_id, callers
 * should skip opening this dialog and navigate to the existing job.
 */
export default function ScheduleJobDialog({ open, onClose, estimate, onScheduled }) {
  const [date, setDate] = useState(null);            // Date object (local calendar date)
  const [hour, setHour] = useState("9");             // 0-23 as string
  const [minute, setMinute] = useState("00");        // "00" | "15" | "30" | "45"
  const [duration, setDuration] = useState(90);
  const [assignedTo, setAssignedTo] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Default = tomorrow at 09:00 local
    const t = new Date();
    t.setDate(t.getDate() + 1);
    t.setHours(9, 0, 0, 0);
    setDate(t);
    setHour("9");
    setMinute("00");
    setDuration(90);
    setAssignedTo("");
    setAddress(estimate?.service_address || estimate?.property_address || "");
    setNotes("");
  }, [open, estimate]);

  const submit = async () => {
    if (!estimate?.id) return;
    if (!date) { toast.error("Pick a start date."); return; }
    setSaving(true);
    try {
      // Compose the picked calendar date + hour/minute as America/Chicago
      // wall clock, then convert to UTC ISO. Browser timezone does NOT
      // influence the stored instant — a technician on a phone in Phoenix
      // picking "2 PM" lands at 2 PM Central on the owner's calendar.
      const startIso = composeBusinessIso(date, hour, minute);
      const { data: job } = await api.post(`/estimates/${estimate.id}/convert-to-job`, {
        scheduled_start: startIso,
        duration_minutes: Number(duration) || null,
        assigned_to: assignedTo || null,
        service_address: address || null,
        notes: notes || null,
      });
      toast.success(`Job scheduled for ${formatBusinessDateTime(startIso)}`);
      onScheduled?.(job);
      onClose?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "";
      toast.error(`Schedule failed${status ? ` (${status})` : ""}: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="schedule-job-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Schedule Job {estimate?.estimate_number ? `· ${estimate.estimate_number}` : ""}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Customer, service address, services, totals, and notes are carried over from the estimate.
            An Estimate stays an Estimate — scheduling doesn&apos;t create an invoice.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {estimate && (
            <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded p-2" data-testid="schedule-job-summary">
              Carrying over: <strong>{estimate.customer_name || "customer"}</strong>
              {estimate.total ? <> · <strong>${(estimate.total || 0).toLocaleString()}</strong></> : null}
              {estimate.title ? <> · {estimate.title}</> : null}
            </div>
          )}

          <div>
            <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
              <Clock className="w-3 h-3"/> Start
            </label>
            <div className="grid grid-cols-[1fr_auto_auto] gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal h-10",
                      !date && "text-muted-foreground"
                    )}
                    data-testid="schedule-job-date-btn"
                  >
                    <Calendar className="w-4 h-4 mr-2 text-zinc-500" />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComp
                    mode="single"
                    selected={date}
                    onSelect={(d) => d && setDate(d)}
                    initialFocus
                    data-testid="schedule-job-date-calendar"
                  />
                </PopoverContent>
              </Popover>
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger className="w-[100px]" data-testid="schedule-job-hour"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HOUR_OPTIONS.map((h) => (
                    <SelectItem key={h.value} value={h.value} data-testid={`schedule-job-hour-${h.value}`}>{h.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger className="w-[80px]" data-testid="schedule-job-minute"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MINUTE_OPTIONS.map((m) => (
                    <SelectItem key={m.value} value={m.value} data-testid={`schedule-job-minute-${m.value}`}>:{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-1 block">
              Estimated duration (minutes)
            </label>
            <Input
              type="number"
              min={15}
              step={15}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              data-testid="schedule-job-duration"
            />
          </div>

          <div>
            <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
              <User className="w-3 h-3"/> Assigned technician / team
            </label>
            <Input
              placeholder="e.g. Crew A · Josh"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              data-testid="schedule-job-assigned"
            />
          </div>

          <div>
            <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
              <MapPin className="w-3 h-3"/> Service address (override)
            </label>
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Defaults to customer address"
              data-testid="schedule-job-address"
            />
          </div>

          <NearbyDaysHint
            customerId={estimate?.customer_id}
            address={address}
            onPick={(ymd) => { const [y, m, d] = ymd.split("-").map(Number); setDate(new Date(y, m - 1, d, 9, 0)); setHour("9"); setMinute("00"); }}
            testid="schedule-job-nearby"
          />

          <div>
            <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-1 block">
              Additional notes
            </label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Access instructions, parking, etc."
              data-testid="schedule-job-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="schedule-job-cancel-btn">Cancel</Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={saving}
            onClick={submit}
            data-testid="schedule-job-submit-btn"
          >
            {saving ? "Scheduling…" : "Schedule job"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
